import { Button, Modal, Table, Input, Select, Space, Spin, message } from "antd";
import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Locale } from "../../../utils";
import { showNotificationWithIcon } from "../../../utils/helpers/helpers";
import { getList } from "../../../utils/http/dataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { TYPE_ARTICLE, TYPE_AUDIO, TYPE_VIDEO } from "../courses/courseTypes";

import "./CourseSelectionModal.scss";

const i18n = Locale.getInstance();
const { Option } = Select;

const CourseSelectionModal = ({
  visible,
  onCancel,
  onConfirm,
  selectedCourseIds = [],
  excludeCourseIds = [],
  onCoursesSelected,
}) => {
  const user = useSelector((state) => getUserInfo(state));
  const [keyword, setKeyword] = useState("");
  const [categoryId, setCategoryId] = useState(0);
  const [courseType, setCourseType] = useState(undefined);
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [allSelectedCourses, setAllSelectedCourses] = useState([]);
  const [perPage] = useState(10);

  useEffect(() => {
    if (visible) {
      setSelectedRowKeys(selectedCourseIds);
      setAllSelectedCourses([]);
      setKeyword("");
      setCategoryId(0);
      setCourseType(undefined);
      setPage(1);
    }
  }, [visible]);

  const filter = {};
  if (keyword) filter.keyword = keyword;
  if (categoryId) filter.category_id = categoryId;
  if (courseType !== undefined) filter.type = courseType;
  filter.status = 1; // Only show active courses

  const { data, error, isLoading } = useQuery(
    ["courses-selection", user.token, page, perPage, filter],
    () =>
      getList("courses", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter,
        token: user.token,
      }),
    {
      enabled: visible,
    }
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const { data: categoriesData } = useQuery(
    ["course-categories-selection", user.token],
    () =>
      getList("course/categories", {
        pagination: {
          perPage: 1000,
          page: 1,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        token: user.token,
      }),
    {
      enabled: visible,
    }
  );

  const flattenCategories = (categories, result = []) => {
    if (!categories) return result;
    categories.forEach((cat) => {
      result.push(cat);
      if (cat.sub_categories && cat.sub_categories.length > 0) {
        flattenCategories(cat.sub_categories, result);
      }
    });
    return result;
  };

  const categories = categoriesData?.data
    ? flattenCategories(categoriesData.data)
    : [];

  // Filter out excluded courses
  const filteredData = data?.data
    ? data.data.filter((course) => !excludeCourseIds.includes(course.id))
    : [];

  // Initialize allSelectedCourses with courses from current page that match selectedCourseIds
  useEffect(() => {
    if (visible && filteredData.length > 0 && allSelectedCourses.length === 0 && selectedCourseIds.length > 0) {
      const initialSelected = filteredData.filter((course) => 
        selectedCourseIds.includes(course.id)
      );
      if (initialSelected.length > 0) {
        setAllSelectedCourses(initialSelected);
        setSelectedRowKeys(initialSelected.map((c) => c.id));
      }
    }
  }, [visible, filteredData.length, selectedCourseIds.length]);

  // Update selectedRowKeys when page changes to reflect current page selections
  useEffect(() => {
    if (filteredData.length > 0 && allSelectedCourses.length > 0) {
      const currentPageSelectedIds = filteredData
        .filter((course) => allSelectedCourses.some((c) => c.id === course.id))
        .map((course) => course.id);
      setSelectedRowKeys(currentPageSelectedIds);
    }
  }, [page]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
      // Update all selected courses when selection changes
      const currentPageSelected = filteredData.filter((course) =>
        selectedKeys.includes(course.id)
      );
      // Merge with previously selected courses from other pages
      const otherPageCourses = allSelectedCourses.filter(
        (course) => !filteredData.some((c) => c.id === course.id)
      );
      setAllSelectedCourses([...otherPageCourses, ...currentPageSelected]);
    },
    getCheckboxProps: (record) => ({
      disabled: excludeCourseIds.includes(record.id),
    }),
  };

  const handleConfirm = () => {
    // Use allSelectedCourses which includes courses from all pages
    const finalSelectedCourses = allSelectedCourses.length > 0 
      ? allSelectedCourses 
      : filteredData.filter((course) => selectedRowKeys.includes(course.id));

    if (finalSelectedCourses.length === 0) {
      message.warning(i18n.t("course.album.batchSelectWarning"));
      return;
    }

    if (onCoursesSelected) {
      onCoursesSelected(finalSelectedCourses);
    } else if (onConfirm) {
      onConfirm(finalSelectedCourses.map((c) => c.id));
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 60,
    },
    {
      title: i18n.t("course.courseInfo"),
      render: (item) => {
        return (
          <div className="info">
            <div className="cover">
              <img
                src={i18n.getPropNameByLocale(item, "cover")}
                alt={i18n.getPropNameByLocale(item, "name")}
                width={60}
                height={60}
                style={{ objectFit: "cover" }}
              />
            </div>
            <div className="name">
              <span>{i18n.getPropNameByLocale(item, "name")}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: i18n.t("course.type"),
      render: (item) => {
        return <span>{item.type_label}</span>;
      },
      width: 120,
    },
    {
      title: i18n.t("course.categoryName"),
      render: (item) => {
        return <span>{i18n.getPropNameByLocale(item.category, "name") || "-"}</span>;
      },
      width: 120,
    },
    {
      title: i18n.t("course.price"),
      render: (item) => {
        return <span>¥{item.price?.toFixed(2) || "0.00"}</span>;
      },
      width: 100,
    },
  ];

  const calculateTotalPrice = () => {
    const coursesToCalculate = allSelectedCourses.length > 0
      ? allSelectedCourses
      : filteredData.filter((course) => selectedRowKeys.includes(course.id));
    return coursesToCalculate.reduce((sum, course) => sum + (course.price || 0), 0);
  };

  return (
    <Modal
      title={i18n.t("course.album.courseSelection")}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {i18n.t("base.modal.cancelText")}
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          {i18n.t("course.album.addCourses")} ({allSelectedCourses.length > 0 ? allSelectedCourses.length : selectedRowKeys.length})
        </Button>,
      ]}
    >
      <div className="course-selection-modal">
        <div className="filters" style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder={i18n.t("course.name")}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              style={{ width: 200 }}
              allowClear
            />
            <Select
              style={{ width: 150 }}
              placeholder={i18n.t("course.type")}
              allowClear
              value={courseType}
              onChange={(value) => {
                setCourseType(value);
                setPage(1);
              }}
            >
              <Option value={TYPE_ARTICLE}>{i18n.t("course.titleArticle")}</Option>
              <Option value={TYPE_AUDIO}>{i18n.t("course.titleAudio")}</Option>
              <Option value={TYPE_VIDEO}>{i18n.t("course.titleVideo")}</Option>
            </Select>
            <Select
              style={{ width: 200 }}
              placeholder={i18n.t("course.selectCategory")}
              allowClear
              value={categoryId === 0 ? undefined : categoryId}
              onChange={(value) => {
                setCategoryId(value || 0);
                setPage(1);
              }}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {i18n.getPropNameByLocale(cat, "name")}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        <Spin spinning={isLoading}>
          <Table
            className="list"
            rowKey="id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              current: page,
              total: data?.total,
              pageSize: perPage,
              onChange: (newPage) => {
                setPage(newPage);
              },
            }}
            scroll={{
              y: 400,
            }}
          />
        </Spin>

        {(selectedRowKeys.length > 0 || allSelectedCourses.length > 0) && (
          <div
            style={{
              marginTop: 16,
              padding: "12px",
              background: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Space>
              <span>
                {i18n.t("course.album.selectedCount", {
                  count: allSelectedCourses.length > 0 ? allSelectedCourses.length : selectedRowKeys.length,
                })}
              </span>
              <span style={{ marginLeft: 16 }}>
                {i18n.t("course.album.totalCoursesPrice")}:{" "}
                <span style={{ fontWeight: "bold" }}>
                  ¥{calculateTotalPrice().toFixed(2)}
                </span>
              </span>
            </Space>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CourseSelectionModal;

